import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Rocket, Award, TrendingUp, BarChart3, Crosshair, ArrowRight, Users, Globe, Zap, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ownerImg from "../assets/owner.jpg";

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, delay, ease: "easeOut" },
});

const About = () => {
    const navigate = useNavigate();

    const adPlatforms = [
        "Google Ads", "Meta Ads", "LinkedIn Ads", "Twitter/X Ads",
        "YouTube Ads", "Snapchat Ads", "TikTok Ads", "Amazon Ads",
        "Microsoft Ads", "Pinterest Ads"
    ];

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">

            {/* ─── Hero Section ─── */}
            <div className="relative bg-gradient-to-br from-[#00809D] via-[#006d87] to-[#005568] overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
                <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-white/5 rounded-xl rotate-45" />

                <div className="relative z-10 pt-16 sm:pt-20 lg:pt-24 pb-20 sm:pb-24 lg:pb-28 px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 25 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
                            <Award className="w-4 h-4 text-white/90" />
                            <span className="text-white/90 font-medium text-sm">About Adsurg</span>
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-5 leading-tight tracking-tight">
                            Performance Marketing{" "}
                            <span className="block sm:inline">& Strategic Advertising</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                            Helping organizations break past growth limitations with data-driven, results-focused campaigns.
                        </p>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="white" />
                    </svg>
                </div>
            </div>

            {/* ─── Who We Are ─── */}
            <div className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        <motion.div {...fadeUp()}>
                            <div className="inline-flex items-center gap-2 bg-[#00809D]/10 px-4 py-2 rounded-full mb-5">
                                <Rocket className="w-4 h-4 text-[#00809D]" />
                                <span className="text-[#00809D] font-semibold text-sm">Who We Are</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                                Creating Consistent{" "}
                                <span className="text-[#00809D]">Brand Momentum</span>
                            </h2>

                            <div className="space-y-4 text-gray-600 leading-relaxed">
                                <p>
                                    <strong className="text-gray-800">Adsurg</strong> specializes in performance marketing and strategic advertising for organizations looking to break past growth limitations. Our focus is not solely on generating clicks but on creating consistent momentum for brands.
                                </p>
                                <p>
                                    By leveraging advanced data analytics and precision targeting, we help our clients reach the right audience effectively, ensuring impactful and results-driven campaigns.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div {...fadeUp(0.2)} className="space-y-5">
                            {/* Approach Card */}
                            <div className="bg-gradient-to-br from-[#00809D] to-[#006d87] rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="relative">
                                    <Crosshair className="w-8 h-8 mb-4 text-white/90" />
                                    <h3 className="text-xl sm:text-2xl font-bold mb-3">Our Approach</h3>
                                    <p className="text-white/80 leading-relaxed text-sm sm:text-base">
                                        Through a rigorous combination of precision targeting and deep data analytics, we eliminate the guesswork from advertising. Our campaigns are as impactful as they are efficient.
                                    </p>
                                </div>
                            </div>

                            {/* Data Analytics Card */}
                            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                                <BarChart3 className="w-8 h-8 mb-4 text-[#00809D]" />
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Data-Driven Results</h3>
                                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                                    We leverage advanced data analytics and precision targeting to help our clients reach the right audience effectively, ensuring every campaign delivers measurable impact.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* ─── Meet the Owner ─── */}
            <div className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-6xl mx-auto">

                    {/* ── Main Owner Card ── */}
                    <motion.div
                        {...fadeUp()}
                        className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_8px_40px_-12px_rgba(0,128,157,0.12)]"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-12">
                            {/* Image Side */}
                            <div className="lg:col-span-5 relative">
                                <div className="relative h-80 sm:h-96 lg:h-full min-h-[420px]">
                                    <img
                                        src={ownerImg}
                                        alt="Mridul Joshi, Owner of Adsurg"
                                        className="w-full h-full object-cover object-top"
                                    />
                                    {/* Teal accent stripe - desktop only */}
                                    <div className="hidden lg:block absolute top-0 right-0 w-1.5 h-full bg-gradient-to-b from-[#00809D] via-[#00809D]/60 to-transparent" />
                                </div>
                            </div>

                            {/* Content Side */}
                            <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 xl:p-12 flex flex-col justify-center">
                                {/* Name */}
                                <div className="mb-6">
                                    <span className="inline-block bg-[#00809D]/10 text-[#00809D] text-xs font-bold px-3 py-1 rounded-full mb-3 tracking-wide uppercase">Owner of Adsurg</span>
                                    <h2 className="text-3xl xl:text-4xl font-extrabold text-gray-900 tracking-tight">Mridul Joshi</h2>
                                </div>

                                {/* Bio */}
                                <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
                                    <p>
                                        <strong className="text-gray-900">Mridul Joshi</strong> is the owner and strategic mind behind Adsurg. What started as a deep curiosity for digital advertising quickly turned into a full-scale career built on one principle: every rupee a brand spends on ads should bring back measurable results.
                                    </p>
                                    <p>
                                        Having worked hands-on across <strong className="text-gray-800">Google Ads, Meta Ads, YouTube Ads, LinkedIn, Snapchat, TikTok, Amazon</strong> and virtually every major advertising platform, Mridul doesn't just manage campaigns, he architects them. From setting up pixel tracking and conversion APIs to designing high-converting funnels and scaling budgets profitably, he handles the full spectrum of paid media.
                                    </p>
                                    <p>
                                        His deep understanding of ad auction systems, bidding algorithms, audience behavior, and creative psychology allows him to consistently outperform industry benchmarks. Brands that partner with Adsurg don't just get ads, they get a growth engine built by someone who has lived and breathed performance marketing from day one.
                                    </p>
                                </div>

                                {/* Quote */}
                                <div className="mt-6 pl-4 border-l-[3px] border-[#00809D]">
                                    <p className="text-[#00809D] font-semibold text-base italic leading-relaxed">
                                        "Ads are not an expense, they are an investment. My job is to make sure every investment delivers returns that speak for themselves."
                                    </p>
                                </div>

                                {/* Expertise Tags */}
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {["Campaign Strategy", "Data Analytics", "Audience Targeting", "Performance Scaling"].map((tag, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-100"
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#00809D]" />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* ── Platform Mastery Strip ── */}
                    <motion.div {...fadeUp(0.15)} className="mt-8">
                        <div className="bg-gradient-to-br from-[#00809D] to-[#005568] rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                            <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/5 rounded-full" />
                            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />

                            <div className="relative">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mb-6">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
                                            <Globe className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white">All Platforms. One Expert.</h3>
                                    </div>
                                    <div className="hidden sm:block h-px flex-1 bg-white/10" />
                                    <span className="text-white/60 text-sm font-medium">{adPlatforms.length} Platforms Mastered</span>
                                </div>

                                <div className="flex flex-wrap gap-2.5">
                                    {adPlatforms.map((platform, i) => (
                                        <motion.span
                                            key={i}
                                            initial={{ opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.35, delay: i * 0.04 }}
                                            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-white border border-white/10 hover:bg-white/20 transition-all duration-300 cursor-default"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                                            {platform}
                                        </motion.span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* ── Core Strengths ── */}
                    <motion.div {...fadeUp(0.25)} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { icon: <Zap className="w-5 h-5" />, title: "Campaign Strategy", desc: "Full-funnel ad strategies tailored to goals and market." },
                            { icon: <BarChart3 className="w-5 h-5" />, title: "Data Analytics", desc: "Raw data turned into actionable, growth-driving insights." },
                            { icon: <Crosshair className="w-5 h-5" />, title: "Audience Targeting", desc: "Precision segments that maximize relevance and ROI." },
                            { icon: <TrendingUp className="w-5 h-5" />, title: "Scaling Expertise", desc: "Frameworks for scaling ad spend without diminishing returns." },
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.25 }}
                                className="group bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 hover:border-[#00809D]/20 hover:shadow-lg hover:shadow-[#00809D]/8 transition-all duration-300"
                            >
                                <div className="w-11 h-11 rounded-xl bg-[#00809D]/10 flex items-center justify-center text-[#00809D] mb-4 group-hover:bg-[#00809D] group-hover:text-white transition-all duration-300">
                                    {item.icon}
                                </div>
                                <h4 className="font-bold text-gray-900 text-sm mb-1.5">{item.title}</h4>
                                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* ─── Our Mission ─── */}
            <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-5xl mx-auto">
                    <motion.div {...fadeUp()} className="text-center mb-12 sm:mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#00809D]/10 px-4 py-2 rounded-full mb-5">
                            <Target className="w-4 h-4 text-[#00809D]" />
                            <span className="text-[#00809D] font-semibold text-sm">Our Mission</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                            Dismantling Growth Limitations
                        </h2>
                        <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
                            Our mission is to dismantle the limitations holding brands back by connecting them to their ideal audiences through strategic, high-impact advertising.
                        </p>
                    </motion.div>

                    <motion.div {...fadeUp(0.1)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                        {[
                            {
                                icon: <TrendingUp className="w-6 h-6" />,
                                title: "Sustainable Momentum",
                                description: "We create sustainable brand momentum through campaigns that deliver long-term growth, not just short-term spikes.",
                            },
                            {
                                icon: <Crosshair className="w-6 h-6" />,
                                title: "Precision Targeting",
                                description: "Every campaign is powered by precision targeting that connects brands with their ideal audience at the right moment.",
                            },
                            {
                                icon: <BarChart3 className="w-6 h-6" />,
                                title: "Deep Data Analytics",
                                description: "We eliminate guesswork from advertising with rigorous data analytics that inform every strategic decision.",
                            },
                            {
                                icon: <Eye className="w-6 h-6" />,
                                title: "Impactful Campaigns",
                                description: "Our campaigns are crafted to be as impactful as they are efficient, maximizing results while optimizing spend.",
                            },
                            {
                                icon: <Rocket className="w-6 h-6" />,
                                title: "Break Growth Barriers",
                                description: "We help organizations looking to break past growth limitations and reach new heights with strategic advertising.",
                            },
                            {
                                icon: <Target className="w-6 h-6" />,
                                title: "Results-Driven Focus",
                                description: "Our focus is not solely on generating clicks but on creating consistent, measurable results for every brand.",
                            },
                        ].map((value, i) => (
                            <motion.div
                                key={i}
                                {...fadeUp(i * 0.1)}
                                className="group bg-white rounded-2xl p-6 sm:p-7 border border-gray-100 hover:border-[#00809D]/20 hover:shadow-lg hover:shadow-[#00809D]/5 hover:-translate-y-1 transition-all duration-400"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#00809D]/10 flex items-center justify-center text-[#00809D] mb-5 group-hover:bg-[#00809D] group-hover:text-white transition-all duration-300">
                                    {value.icon}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* ─── Bottom CTA ─── */}
            <div className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        {...fadeUp()}
                        className="relative bg-gradient-to-br from-[#00809D] to-[#006d87] rounded-3xl p-8 sm:p-10 lg:p-12 text-center overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                        <div className="relative">
                            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                                Ready to Break Past Your Growth Limits?
                            </h3>
                            <p className="text-white/80 text-base sm:text-lg mb-8 max-w-xl mx-auto">
                                Let's connect your brand with the right audience through strategic, high-impact advertising.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                                <button
                                    onClick={() => navigate('/contact')}
                                    className="w-full sm:w-auto bg-white text-[#00809D] px-8 py-4 rounded-xl font-bold text-base hover:bg-gray-50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-lg shadow-black/10 flex items-center justify-center gap-2"
                                >
                                    Get in Touch
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default About;
